import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  useTheme
} from "@material-ui/core";
import { RouteComponentProps } from "@reach/router";
import { Link } from "gatsby";
import React from "react";
import { useInView } from "react-intersection-observer";
import LocalizedLink from "../../../../../components/localized-link";
import { getProductDetailsPath } from "../../../../../helpers/url";
import useIsAuthenticated from "../../../../../hooks/use-is-authenticated";
import usePaymentMethods from "../../../../../hooks/use-payment-methods";
import useProductQuery from "../../../../../hooks/use-product-query";
import ExpressDeliveryLogo from "../../../../express-delivery/ExpressDeliveryLogo";
import ProductDetailsContentModules from "../../../../shop/ProductDetails/components/content-modules";
import ProductDetailsOilTable from "../../../../shop/ProductDetails/components/Overview/OilTable";
import { generateOilDynamicContentModules } from "../../../../shop/ProductDetails/utils";
import ActivationHeading from "../../../components/ActivationHeading";
import ActivationLayout from "../../../components/Layout";
import { getFullWindowMinHeight } from "../../../components/Layout/utils";
import PodProductCard from "../../../components/PodProductCard";
import { usePodDetails } from "../../../hooks/usePodDetails";
import useUnlockAllowed from "../../../hooks/useUnlockAllowed";
import { getActivationPath } from "../../../utils/url";

type Props = RouteComponentProps<{ podKeyOrSerialNumber: string }>;

export default function ActivationStep1PodInfo({
  podKeyOrSerialNumber
}: Props) {
  const theme = useTheme();

  // Pod details
  const [podDetails] = usePodDetails(podKeyOrSerialNumber);
  const { data: podProduct, isLoading: isLoadingPodProduct } = useProductQuery({
    batchId: podDetails?.batchId
  });
  const podProductVariant = podProduct?.variants?.find(
    v => v.batchId === podDetails?.batchId
  );

  // Auth & subscription
  const isAuthenticated = useIsAuthenticated();

  // Unlock permission
  const { isUnlockAllowed, isLoadingUnlockAllowed } = useUnlockAllowed(
    podKeyOrSerialNumber
  );

  // Check if user has existing payment method (If not allowed to unlock)
  const {
    data: paymentMethods,
    isLoading: isLoadingPaymentMethods
  } = usePaymentMethods({
    enabled: isUnlockAllowed === false
  });
  const hasExistingPaymentMethod = paymentMethods?.length > 0;

  // CTA link & state
  const ctaText = "Unlock my pod";
  let ctaPath = `${podKeyOrSerialNumber}/sign-in`;
  if (isUnlockAllowed) {
    ctaPath = `${podKeyOrSerialNumber}/activation`;
  } else if (isAuthenticated && hasExistingPaymentMethod) {
    ctaPath = `${podKeyOrSerialNumber}/payment`;
  } else if (isAuthenticated) {
    ctaPath = `${podKeyOrSerialNumber}/payment-info`;
  }

  // Show loading while fetching pod details or checking membershipSubscription
  const showLoading = !podDetails || isLoadingPodProduct;
  const showCtaLoading = isAuthenticated
    ? isLoadingUnlockAllowed || isLoadingPaymentMethods
    : false;

  const [ctaInViewRef, ctaInView] = useInView({
    threshold: 0.75,
    initialInView: true
  });
  const showStickyCta = !ctaInView;

  const ctaContent = showCtaLoading ? (
    <CircularProgress size={16} color="secondary" />
  ) : (
    ctaText
  );

  return (
    <>
      <ActivationLayout showLoading={showLoading} paddingBottom={0}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          minHeight={{
            xs: getFullWindowMinHeight({ offset: 48, initial100vh: true }),
            sm: 0
          }}
        >
          {/* Pod details */}
          {/* Title & description */}
          <Box mb={{ xs: 2, sm: 2, md: 4 }}>
            {!isUnlockAllowed && (
              <ActivationHeading>
                {
                  "Next, let's set up your Airgraft Membership to unlock this pod."
                }
              </ActivationHeading>
            )}

            {isUnlockAllowed && (
              <ActivationHeading>
                {"Most excellent choice! Your pod is ready to unlock."}
              </ActivationHeading>
            )}
          </Box>

          {/* Pod overview (Cover, title + brand, Oil stats table) */}
          <Box display="flex" flexDirection="column">
            {/* Pod info card */}
            <PodProductCard
              productVariant={podProductVariant}
              podKey={podDetails?.podKey}
            />

            {/* Pod title & brand */}
            <Box textAlign="center" mt={{ xs: 1.5, md: 3 }}>
              <Typography variant="h5" component="span">
                {podProductVariant?.title}
              </Typography>
            </Box>

            {/* Oil stats table */}
            {podProductVariant && (
              <Box mt={{ xs: 1.5, md: 3 }}>
                <ProductDetailsOilTable productVariant={podProductVariant} />
              </Box>
            )}
          </Box>

          {/* Primary CTA */}
          <Box mt={{ xs: 2, md: 3 }}>
            <Box mb={{ xs: 1.5, md: 2 }}>
              {/* @ts-ignore */}
              <Button
                variant="contained"
                fullWidth
                color="primary"
                component={Link}
                to={getActivationPath(ctaPath)}
                disabled={showCtaLoading}
                ref={ctaInViewRef}
                style={{ visibility: showStickyCta ? "hidden" : "visible" }}
              >
                {ctaContent}
              </Button>
            </Box>

            {/* Secondary CTA */}
            {podProductVariant && (
              <Button
                variant="contained"
                fullWidth
                component={LocalizedLink}
                to={getProductDetailsPath(
                  podProduct.productType,
                  podProduct.slug
                )}
                endIcon={<ExpressDeliveryLogo />}
                data-analytics="activation.pod-info.shop-with-express"
              >
                Shop with
              </Button>
            )}
          </Box>
        </Box>

        {/* Sticky Primary CTA */}
        <Box
          width="100%"
          position="fixed"
          top={{ xs: "unset", md: 0 }}
          bottom={{ xs: 0, md: "unset" }}
          left={0}
          padding={{ xs: 1.5, md: 2 }}
          zIndex={1200}
          bgcolor={alpha(theme.palette.grey[200], 0.7)}
          display="flex"
          justifyContent="center"
          style={{
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            opacity: showStickyCta ? 1 : 0,
            pointerEvents: showStickyCta ? "all" : "none",
            transition: "opacity 200ms"
          }}
        >
          <Container maxWidth="sm" disableGutters>
            <Button
              variant="contained"
              fullWidth
              color="primary"
              component={Link}
              to={getActivationPath(ctaPath)}
              disabled={showCtaLoading}
            >
              {ctaContent}
            </Button>
          </Container>
        </Box>
      </ActivationLayout>

      {/* Pod content modules */}
      {podProductVariant?.variantDetail?.coaReport && (
        <Box
          maxWidth={{
            xs: "none",
            sm: theme.breakpoints.values.sm,
            md: theme.breakpoints.values.md,
            lg: theme.breakpoints.values.md
          }}
          marginX="auto"
          marginY={{ xs: 6, sm: 6, md: 12 }}
        >
          <ProductDetailsContentModules
            id="pod-content-modules"
            contentModules={generateOilDynamicContentModules(podProductVariant)}
          />
        </Box>
      )}
    </>
  );
}
